/**
 * Llamadas HTTP del dominio Administración (sedes + atendidos + exportación).
 *
 * SIEMPRE sobre el cliente único `lib/api` (`docs/frontend_architecture.md`,
 * Pr. 2): ningún `fetch`/`ky` suelto. El cliente aporta `credentials: 'include'`,
 * el `X-CSRF-TOKEN` en las mutaciones y el `X-Sede-Id` (sede activa) en cada
 * request.
 *
 * - **Sedes**: el recurso ES la sede (catálogo global del admin); el backend NO
 *   exige `X-Sede-Id` aquí (igual que el SSR, que lista todas las sedes).
 * - **Atendidos**: el atendido PERTENECE a una sede; el backend filtra/acota por
 *   el header `X-Sede-Id` (la sede activa), que el cliente ya inyecta. Por eso
 *   estas llamadas dependen de la sede activa.
 * - **Exportación**: la respuesta de éxito es un binario `.xlsx` (no JSON); se
 *   acota por la sede activa (`X-Sede-Id`), igual que el resto.
 *
 * Un `ApiError` (401/403/404/4xx/5xx) lo traduce el cliente y lo gestionan los
 * hooks/pantallas (`admin/format.ts`). Cubre `application/api/domains/sedes.py`,
 * `atendidos.py` y `export.py` (#29/#30/#36).
 */
import { api } from "@/lib/api";

import type {
  AtendidoDetail,
  AtendidosList,
  CreateAtendidoBody,
  CreateSedeBody,
  ExportFile,
  ExportScope,
  SedeDetail,
  SedesList,
  UpdateAtendidoBody,
  UpdateSedeBody,
} from "./types";

// --------------------------------------------------------------------------
// Sedes
// --------------------------------------------------------------------------

/**
 * Lista el catálogo de sedes con sus estadísticas (`GET /sedes`).
 *
 * Permiso backend: `all`. Cada item trae el bloque `stats`
 * (usuarios/órdenes/pendientes/trabajadores).
 */
export async function fetchSedes(): Promise<SedesList> {
  const response = await api.get("sedes");
  return (await response.json()) as SedesList;
}

/**
 * Detalle de una sede (`GET /sedes/<id>`).
 *
 * Permiso backend: `all`. Vista `detail`: datos de contacto y timestamps.
 */
export async function fetchSede(sedeId: number): Promise<SedeDetail> {
  const response = await api.get(`sedes/${sedeId}`);
  return (await response.json()) as SedeDetail;
}

/**
 * Crea una sede (`POST /sedes`).
 *
 * Permiso backend: `all`. La validación (obligatorios, unicidad de nombre) la
 * hace el backend; devuelve 201 con la vista `detail`.
 */
export async function createSede(body: CreateSedeBody): Promise<SedeDetail> {
  const response = await api.post("sedes", { json: body });
  return (await response.json()) as SedeDetail;
}

/**
 * Edita una sede (`PATCH /sedes/<id>`).
 *
 * Permiso backend: `all`. PATCH parcial: solo viajan los campos a cambiar.
 * Devuelve la vista `detail`.
 */
export async function updateSede(
  sedeId: number,
  body: UpdateSedeBody,
): Promise<SedeDetail> {
  const response = await api.patch(`sedes/${sedeId}`, { json: body });
  return (await response.json()) as SedeDetail;
}

/**
 * Elimina una sede (`DELETE /sedes/<id>`).
 *
 * Permiso backend: `all`. Solo si no tiene datos asociados
 * (usuarios/órdenes/talonarios); 409 si los tiene. 204 sin cuerpo.
 */
export async function deleteSede(sedeId: number): Promise<void> {
  await api.delete(`sedes/${sedeId}`);
}

// --------------------------------------------------------------------------
// Atendidos (acotados por la sede activa vía `X-Sede-Id`)
// --------------------------------------------------------------------------

/**
 * Lista los atendidos activos de la sede activa (`GET /atendidos`).
 *
 * Permiso backend: `all`. El backend filtra por el header `X-Sede-Id` que el
 * cliente inyecta (la sede activa); lista SOLO los activos, igual que el SSR.
 */
export async function fetchAtendidos(): Promise<AtendidosList> {
  const response = await api.get("atendidos");
  return (await response.json()) as AtendidosList;
}

/**
 * Detalle de un atendido (`GET /atendidos/<id>`).
 *
 * Permiso backend: `all`. 404 si no existe o pertenece a otra sede.
 */
export async function fetchAtendido(atendidoId: number): Promise<AtendidoDetail> {
  const response = await api.get(`atendidos/${atendidoId}`);
  return (await response.json()) as AtendidoDetail;
}

/**
 * Crea un atendido en la sede activa (`POST /atendidos`).
 *
 * Permiso backend: `all`. El atendido se crea en la sede resuelta del request
 * (`X-Sede-Id`): la sede no va en el cuerpo. La validación (nombre obligatorio,
 * unicidad por sede) la hace el backend; devuelve 201 con la vista `detail`.
 */
export async function createAtendido(
  body: CreateAtendidoBody,
): Promise<AtendidoDetail> {
  const response = await api.post("atendidos", { json: body });
  return (await response.json()) as AtendidoDetail;
}

/**
 * Edita un atendido (`PATCH /atendidos/<id>`).
 *
 * Permiso backend: `all`. PATCH parcial; el atendido permanece en su sede (la
 * API no mueve atendidos entre sedes). Devuelve la vista `detail`.
 */
export async function updateAtendido(
  atendidoId: number,
  body: UpdateAtendidoBody,
): Promise<AtendidoDetail> {
  const response = await api.patch(`atendidos/${atendidoId}`, { json: body });
  return (await response.json()) as AtendidoDetail;
}

/**
 * Elimina un atendido (`DELETE /atendidos/<id>`).
 *
 * Permiso backend: `all`. Solo si no tiene órdenes asociadas; 409 con el conteo
 * exacto si las tiene. 204 sin cuerpo.
 */
export async function deleteAtendido(atendidoId: number): Promise<void> {
  await api.delete(`atendidos/${atendidoId}`);
}

// --------------------------------------------------------------------------
// Exportación a Excel (binario)
// --------------------------------------------------------------------------

/** Nombre de archivo por defecto si la respuesta no trae `Content-Disposition`. */
const DEFAULT_EXPORT_FILENAME = "ordenes.xlsx";

/**
 * Extrae el `filename` de la cabecera `Content-Disposition` que arma el backend
 * (`send_file(..., download_name=...)`). Soporta tanto `filename="..."` como la
 * variante codificada `filename*=UTF-8''...`. Cae a un nombre por defecto si no
 * viene la cabecera (no debería: el backend siempre la fija).
 */
export function filenameFromDisposition(header: string | null): string {
  if (!header) {
    return DEFAULT_EXPORT_FILENAME;
  }
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // Si la decodificación falla, se intenta la variante simple más abajo.
    }
  }
  const simple = /filename="?([^";]+)"?/i.exec(header);
  if (simple?.[1]) {
    return simple[1];
  }
  return DEFAULT_EXPORT_FILENAME;
}

/**
 * Descarga el Excel de órdenes (`GET /export/today` o `GET /export/all`).
 *
 * Permiso backend: `all`. La respuesta de éxito es el binario `.xlsx`; se acota
 * por la sede activa (`X-Sede-Id`, inyectado por el cliente). Se devuelve el
 * `blob` y el `filename` (de `Content-Disposition`) para que el hook dispare la
 * descarga en el navegador. En error (404 sin órdenes, 500 sin openpyxl) el
 * cliente ya lanza `ApiError` con el contrato JSON.
 */
export async function exportOrders(scope: ExportScope): Promise<ExportFile> {
  const response = await api.get(`export/${scope}`);
  const blob = await response.blob();
  const filename = filenameFromDisposition(
    response.headers.get("Content-Disposition"),
  );
  return { blob, filename };
}
